import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  LinearProgress,
  Skeleton,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { EmptyState, FilterChipBarV2 } from '../../../components/v2';
import { normalizeText } from '../../../components/v2/selectSearchFilter';
import BaseService from '../../../services/BaseService';
import * as actionsRelatorioJob from '../../../store/relatorioJob/actions';
import * as selectorsRelatorioJob from '../../../store/relatorioJob/reducer';
import {
  RELATORIO_JOB_PAGE_SIZE,
  RELATORIO_JOB_POLL_MS,
  getJobSubtitle,
  isRelatorioJobAvailable,
  isRelatorioJobInFlight,
  parseRelatorioJobsPage,
  filterDownloadableRelatorioJobs,
} from '../../../helpers/relatorioJob';
import RelatorioJobStatusIcon from './RelatorioJobStatusIcon';
import RelatorioJobDownloadButton from './RelatorioJobDownloadButton';

/** Busca local: carrega mais itens para cobrir além da página atual. */
const RELATORIO_JOB_SEARCH_SIZE = 100;

const formatWhen = (value) => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '';
};

const matchesTitulo = (titulo, query) => {
  const term = normalizeText(query.trim());
  if (!term) return true;
  return normalizeText(titulo).includes(term);
};

const DownloadsListSkeleton = ({ rows = 5 }) => (
  <Box
    sx={{
      width: '100%',
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    {Array.from({ length: rows }).map((_, index) => (
      <Stack
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{
          px: 1.5,
          py: 1.5,
          borderBottom: (theme) => (
            index === rows - 1 ? 0 : `1px solid ${theme.palette.divider}`
          ),
        }}
      >
        <Skeleton
          variant="circular"
          width={28}
          height={28}
          sx={{ flexShrink: 0, mt: 0.25 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton width="45%" height={18} />
          <Skeleton width="70%" height={14} sx={{ mt: 0.75 }} />
          <Skeleton width="25%" height={12} sx={{ mt: 0.75 }} />
        </Box>
        <Skeleton
          variant="rounded"
          width={108}
          height={32}
          sx={{ flexShrink: 0 }}
        />
      </Stack>
    ))}
  </Box>
);

DownloadsListSkeleton.propTypes = {
  rows: PropTypes.number,
};

DownloadsListSkeleton.defaultProps = {
  rows: 5,
};

const DownloadsV2 = ({ downloadRelatorioJob, downloadingId }) => {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const loadRequestIdRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setBuscaDebounced(busca), 300);
    return () => window.clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    setPage(0);
  }, [buscaDebounced]);

  const hasBusca = Boolean(buscaDebounced.trim());

  const load = useCallback(async (silent = false) => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    if (!silent) setLoading(true);
    try {
      const pageParam = hasBusca ? 0 : page;
      const sizeParam = hasBusca ? RELATORIO_JOB_SEARCH_SIZE : RELATORIO_JOB_PAGE_SIZE;
      const response = await BaseService.get({
        url: `reports/jobs?p=${pageParam}&s=${sizeParam}`,
      });
      if (requestId !== loadRequestIdRef.current) return;
      const parsed = parseRelatorioJobsPage(response.data);
      const items = filterDownloadableRelatorioJobs(parsed.items);
      setRows(items);
      setTotal(
        hasBusca
          ? items.length
          : (Number.isFinite(Number(parsed.totalElements))
            ? Number(parsed.totalElements)
            : items.length)
      );
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) return;
      if (!silent) {
        setRows([]);
        setTotal(0);
      }
    }
    if (requestId === loadRequestIdRef.current && !silent) setLoading(false);
  }, [page, hasBusca]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const shouldPoll = rows.some((job) => isRelatorioJobInFlight(job.status));
    if (!shouldPoll) return undefined;
    const timer = window.setInterval(() => {
      load(true);
    }, RELATORIO_JOB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [rows, load]);

  const filteredRows = useMemo(
    () => rows.filter((job) => matchesTitulo(job.titulo, buscaDebounced)),
    [rows, buscaDebounced]
  );

  const displayCount = hasBusca ? filteredRows.length : total;
  const emptyTitle = hasBusca ? 'Nenhum resultado' : 'Nenhum relatório gerado';
  const emptyMessage = hasBusca
    ? 'Tente outro título ou limpe a busca.'
    : 'Os arquivos que você solicitar aparecerão aqui.';

  return (
    <Box>
      <Stack
        spacing={3}
        sx={{
          maxWidth: 1300,
          mx: 'auto',
          width: '100%',
        }}
      >
        <Box>
          <Typography variant="h4">Downloads</Typography>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {hasBusca ? filteredRows.length : displayCount}
            {' '}
            {(hasBusca ? filteredRows.length : displayCount) === 1 ? 'arquivo' : 'arquivos'}
            {hasBusca && rows.length > 0 ? ` encontrados (até ${RELATORIO_JOB_SEARCH_SIZE} recentes)` : ''}
          </Typography>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <FilterChipBarV2
            searchLabel="Buscar"
            searchValue={busca}
            onSearchChange={setBusca}
            searchPlaceholder="Título do arquivo"
            filterActive={Boolean(busca.trim())}
            showCount={false}
            onClear={() => {
              setBusca('');
              setBuscaDebounced('');
            }}
          />
          {loading && rows.length > 0 && (
            <LinearProgress
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 2,
              }}
            />
          )}
        </Box>

        {loading && rows.length === 0 && (
          <DownloadsListSkeleton />
        )}

        {!loading && filteredRows.length === 0 && (
          <EmptyState
            title={emptyTitle}
            message={emptyMessage}
          />
        )}

        {filteredRows.length > 0 && (
          <Box
            sx={{
              width: '100%',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {filteredRows.map((job) => (
              <Box
                key={job.id}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  px: 1.5,
                  py: 1.5,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  '&:last-of-type': { borderBottom: 0 },
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <RelatorioJobStatusIcon status={job.status} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {job.titulo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {getJobSubtitle(job)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatWhen(job.dataCriacao)}
                  </Typography>
                </Box>
                {isRelatorioJobAvailable(job.status) && (
                  <RelatorioJobDownloadButton
                    job={job}
                    downloadingId={downloadingId}
                    onDownload={downloadRelatorioJob}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}

        {!hasBusca && total > RELATORIO_JOB_PAGE_SIZE && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={RELATORIO_JOB_PAGE_SIZE}
            rowsPerPageOptions={[RELATORIO_JOB_PAGE_SIZE]}
            labelRowsPerPage=""
          />
        )}
      </Stack>
    </Box>
  );
};

DownloadsV2.propTypes = {
  downloadRelatorioJob: PropTypes.func.isRequired,
  downloadingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DownloadsV2.defaultProps = {
  downloadingId: null,
};

const mapStateToProps = (state) => ({
  downloadingId: selectorsRelatorioJob.getRelatorioJobDownloadingId(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsRelatorioJob }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(DownloadsV2);
